import { useState } from "react";
import { motion } from "framer-motion";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: "easeOut" },
  }),
};

// 🔥 Just change this to update your API endpoint
const BASE_URL = "https://gitsetcode-ten.vercel.app/send-email";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
    phone: "",
    organization: "",
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({
        type: "error",
        msg: "Name, Email, and Message are required.",
      });
      return;
    }

    console.log("fpmr", form);

    try {
      setLoading(true);
      setStatus(null);

      const res = await fetch(BASE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) throw new Error("Something went wrong");

      setStatus({ type: "success", msg: "Message sent successfully!" });
      setForm({
        name: "",
        email: "",
        message: "",
        phone: "",
        organization: "",
      });
    } catch (error) {
      setStatus({ type: "error", msg: "Failed to send message. Try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="flex flex-col items-center px-8 lg:px-28 mt-10">
      {/* Light radial background glow */}
      <div className="absolute top-0 left-0 w-full h-[300px] bg-gradient-to-b from-primary-color/10 to-transparent blur-3xl pointer-events-none" />

      {/* Hero Title */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="py-12 mt-10 text-center max-w-2xl relative z-10"
      >
        <p className="text-4xl md:text-5xl font-extrabold text-primary-color drop-shadow-sm">
          CONTACT US
        </p>
        <p className="text-lg text-gray-700 mt-4 leading-relaxed">
          Let’s work together! Whether you need technical help or want to build
          something new — we're here to support you every step of the way.
        </p>
      </motion.div>

      <div className="w-24 h-[3px] bg-primary-color rounded-full mb-12 opacity-80" />

      {/* Form */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeInUp}
        className="w-full lg:w-[55%] bg-gray-50 border border-gray-200 shadow-xl p-10 rounded-3xl mb-20"
      >
        <p className="text-2xl font-semibold mb-6 text-gray-900">
          Send us a Message
        </p>

        {/* Row 1 */}
        <div className="flex flex-col md:flex-row gap-4 w-full">
          <div className="flex-grow">
            <label className="block text-gray-700 text-sm font-semibold mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:border-primary-color"
            />
          </div>

          <div className="flex-grow">
            <label className="block text-gray-700 text-sm font-semibold mb-1">
              Organization
            </label>
            <input
              type="text"
              name="organization"
              value={form.organization}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:border-primary-color"
            />
          </div>
        </div>

        {/* Row 2 */}
        <div className="flex flex-col md:flex-row gap-4 w-full mt-4">
          <div className="flex-grow">
            <label className="block text-gray-700 text-sm font-semibold mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:border-primary-color"
            />
          </div>

          <div className="flex-grow">
            <label className="block text-gray-700 text-sm font-semibold mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:border-primary-color"
            />
          </div>
        </div>

        {/* Message */}
        <div className="mt-4">
          <label className="block text-gray-700 text-sm font-semibold mb-1">
            Message *
          </label>
          <textarea
            name="message"
            value={form.message}
            onChange={handleChange}
            rows="4"
            className="w-full px-4 py-2 border rounded-xl focus:outline-none focus:border-primary-color"
          />
        </div>

        {/* Status Message */}
        {status && (
          <p
            className={`mt-4 text-sm font-semibold ${
              status.type === "error" ? "text-red-500" : "text-green-600"
            }`}
          >
            {status.msg}
          </p>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          className={`w-full px-4 py-3 mt-6 rounded-xl font-semibold text-white 
                        ${
                          loading
                            ? "bg-primary-color/60"
                            : "bg-primary-color hover:bg-opacity-90"
                        } 
                        transition`}
        >
          {loading ? "Sending..." : "Submit"}
        </button>
      </motion.div>

      {/* Bottom Email */}
      <p className="text-center text-gray-700 mb-10">
        You can also email us at{" "}
        <a
          className="text-primary-color font-semibold hover:underline"
          href="mailto:gitsetcode.softwaresolutions@gmail.com"
        >
          gitsetcode.softwaresolutions@gmail.com
        </a>
      </p>

      <div className="h-[2px] w-full bg-gradient-to-r from-transparent via-gray-300 to-transparent mb-12" />
    </section>
  );
}
